// VideoFrameScrubber.tsx
import { useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid2,
  Slider,
} from "@mui/material";
import UCVideoSlider from "./UCVideoSlider";
import DraggableVideoWrapper from "./DraggableVideo";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
// @ts-ignore
import CopyIframeUrlButton from "./CopyIframeUrlButton";
import SyncedVideo from "./SyncedVideo";
import { SLIDER_PRECISION, useVideoPlayerState } from "../../hooks/video/useVideoPlayerState";
import { useVideoControls } from "../../hooks/video/useVideoControls";
import { useCarouselContext } from "../../context/CarouselContext";


const VideoFrameScrubber: React.FC = () => {
  const { 
    filteredImages, 
    ucTime, 
    ucImageSrc, 
    inModal, 
    setUcImageSrc, 
    setUcTime, 
    setInModal, 
    setIsUcViewerActive 
  } = useCarouselContext();

  const videoUrls = useMemo(() => {
    if (inModal) {
      return ucImageSrc ? [ucImageSrc] : [];
    }

    return filteredImages
      .filter(img => img.imageType?.includes("uc"))
      .sort((a, b) => (a.src.toLowerCase().includes("right") ? -1 : 1))
      .map(img => img.src);

  }, [filteredImages, inModal, ucImageSrc]);

  const hasMultipleVideos = videoUrls.length > 1;

  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const singleVideoRef = useRef<HTMLVideoElement | null>(null);

  const playerState = useVideoPlayerState({
    initialTime: ucTime,
    videoRefs,
    singleVideoRef,
    hasMultipleVideos,
  });

  const {
    duration,
    currentTime,
    sliderValue,
    isPlaying,
    isLoaded,
    setDuration,
    setIsLoaded,
    setIsPlaying,
  } = playerState;

  const {
    handleVideoTimeUpdate,
    togglePlay,
    handleSliderDrag,
    handleSliderChangeCommitted,
    captureFrame,
  } = useVideoControls({ playerState });

  const handleFrameClick = (videoUrl: string, time: number) => {
    if (!inModal) {
      setUcTime(time);
      setUcImageSrc(videoUrl);
      setInModal(true);
      setIsUcViewerActive(true);
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        width: "100%",
        my: 0,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Video Display Area */}
      <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
        <Grid2 container spacing={2} sx={{ height: "100%" }}>
          {/* Vertical Slider for Single Video (if not multi-video) */}
          {!hasMultipleVideos && (
            <Grid2 size={{ xs: inModal ? 2 : 3 }}>
              <UCVideoSlider
                value={sliderValue}
                onChange={handleSliderDrag}
                onChangeCommitted={handleSliderChangeCommitted}
                disabled={isPlaying}
              />
            </Grid2>
          )}

          {/* Main Video Container */}
          <Grid2
            size={{ xs: hasMultipleVideos ? 10 : inModal ? 10 : 9 }}
            sx={{
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: inModal ? "solid 2px #999" : "none",
              borderRadius: "5px",
              height: "auto",
              maxHeight: "65vh",
              flexGrow: 1,
              position: "relative",
            }}
          >
            {hasMultipleVideos ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "nowrap",
                  gap: 2,
                  width: "auto",
                  height: "100%",
                  maxHeight: "65vh",
                  aspectRatio: "8/3",
                }}
              >
                {videoUrls.map((src, i) => (
                  <SyncedVideo
                    key={src}
                    src={src}
                    isPlaying={isPlaying}
                    currentTime={currentTime}
                    multiVid={true}
                    onLoaded={(videoDuration) => {
                      if (i === 0) {
                        setDuration(videoDuration);
                        setIsLoaded(true);
                      }
                    }}
                    onFrameClick={handleFrameClick}
                    onPlayPauseChange={setIsPlaying}
                    onTimeUpdate={handleVideoTimeUpdate}
                    onEnded={() => {
                      playerState.getVideos().forEach((v) => {
                        if (v) {
                          v.pause();
                          v.currentTime = 0;
                        }
                      });
                      console.log("onEnded Called")
                      setIsPlaying(false);
                      playerState.setCurrentTime(0);
                      playerState.setSliderValue(0);
                    }}
                    storeRef={(ref) => {
                      if (videoRefs.current) {
                        videoRefs.current[i] = ref;
                      }
                    }}
                  />
                ))}
              </Box>
            ) : inModal ? (
              <DraggableVideoWrapper
                videoRef={singleVideoRef}
                videoUrl={videoUrls[0]}
                currentTime={currentTime}
                isPlaying={isPlaying}
                onPlayPauseChange={setIsPlaying}
                onEnded={() => {
                  setIsPlaying(false);
                  playerState.setSliderValue(0);
                  playerState.setCurrentTime(0);
                }}
                onLoaded={(duration) => {
                  setDuration(duration);
                  setIsLoaded(true);
                }}
                onTimeUpdate={handleVideoTimeUpdate}
              />
            ) : (
              <SyncedVideo
                src={videoUrls[0]}
                isPlaying={isPlaying}
                currentTime={currentTime}
                multiVid={false}
                onLoaded={(videoDuration) => {
                  setDuration(videoDuration);
                  setIsLoaded(true);
                }}
                onFrameClick={handleFrameClick}
                onPlayPauseChange={setIsPlaying}
                onTimeUpdate={handleVideoTimeUpdate}
                onEnded={() => {
                  console.log("onEnded Called")
                  setIsPlaying(false);
                  playerState.setCurrentTime(0);
                  playerState.setSliderValue(0);
                }}
                storeRef={(ref) => {
                  if (singleVideoRef) {
                    singleVideoRef.current = ref;
                  }
                }}
              />
            )}
          </Grid2>
        </Grid2>
      </Box>

      {/* Horizontal Slider for Multi-Video Mode */}
      {hasMultipleVideos && (
        <Box sx={{ display: "flex", width: "100%", justifyContent: "center", mt: 1 }}>
          <Slider
            value={sliderValue}
            onChange={handleSliderDrag}
            onChangeCommitted={handleSliderChangeCommitted}
            min={0}
            max={SLIDER_PRECISION}
            sx={{
              height: 3,
              width: "100%",
              "& .MuiSlider-track": { backgroundColor: "red" },
              "& .MuiSlider-thumb": {
                backgroundColor: "red",
                width: 12,
                height: 12,
                "&:hover": { boxShadow: "0 0 0 8px rgba(255,0,0,0.16)" },
              },
              "& .MuiSlider-rail": { backgroundColor: "red", opacity: 0.2 },
            }}
          />
        </Box>
      )}

      {/* Playback Controls */}
      {isLoaded && (
        <Box
          mt={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
        >
          <Typography variant="body2" color="text.secondary">
            {`Time: ${currentTime.toFixed(2)}s / ${duration.toFixed(
              2
            )}s (${((currentTime / duration) * 100).toFixed(1)}%)`}
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="contained"
              onClick={togglePlay}
              size="small"
              sx={{ minWidth: "40px", padding: "6px" }}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </Button>
            <Button
              variant="contained"
              onClick={captureFrame}
              size="small"
              sx={{ minWidth: "40px", padding: "6px" }}
            >
              <CameraAltIcon />
            </Button>
            <CopyIframeUrlButton />
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default VideoFrameScrubber;